/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import dayjs from 'dayjs';
import { isSafari } from 'react-device-detect';

/**
 * Gestisce la manipolazione DOM delle colonne nella vista WorkWeek (Personal):
 * - Nasconde i giorni di chiusura (width=0) iterando su tutti gli utenti
 * - Allarga la colonna del giorno selezionato e del successivo
 *
 * Aspetta che isLoading sia false e usa un delay per girare
 * DOPO che Syncfusion ha completato il refresh() del DOM.
 *
 * Differenza rispetto al Default: itera su calendarPerUser.keys
 * per verificare se almeno un utente ha orari per quel giorno.
 */
export function useWorkWeekColumns(
  view: string,
  selectedDate: Date,
  calendarPerUser: any,
  scheduleElementId: string,
  isLoading: boolean
) {
  useEffect(() => {
    if (view !== 'WorkWeek') return;
    if (isLoading) return;

    // Delay per assicurarsi che Syncfusion abbia completato refresh()
    // (useSchedulerRefresh usa setTimeout 100ms, questo gira dopo)
    const timeoutId = setTimeout(() => {
      const daysOfWeek =
        dayjs(selectedDate).day() !== 0 ? dayjs(selectedDate).day() - 1 : 6;

      const table = document.getElementById(scheduleElementId);
      if (table === null) return;

      const colgroup = document.getElementsByTagName('colgroup');
      if (colgroup !== null && colgroup.length > 0)
        for (let i = 0; i < colgroup.length; i++) {
          const children = colgroup[i].children;

          if (children && children.length === 7) {
            // Toglie i giorni di chiusura dal calendario
            // Personal: controlla TUTTI gli utenti in calendarPerUser.keys
            if (calendarPerUser && !isSafari) {
              let startRange = dayjs(selectedDate)
                .startOf('date')
                .locale('it')
                .startOf('week');

              let j = 0;
              while (j < 7) {
                let exists = false;
                for (const key of calendarPerUser.keys ?? []) {
                  if (
                    startRange.format('YYYY-MM-DD') in
                      calendarPerUser.values[key] &&
                    (calendarPerUser.values[key][
                      startRange.format('YYYY-MM-DD')
                    ]?.startTime1 ||
                      calendarPerUser.values[key][
                        startRange.format('YYYY-MM-DD')
                      ]?.startTime2)
                  ) {
                    exists = true;
                  }
                }
                if (!exists) {
                  const index =
                    startRange.day() !== 0 ? startRange.day() - 1 : 6;
                  (children[index] as any).style.width = `0px`;
                  (children[index] as any).style.pointerEvents = 'none';
                }
                startRange = startRange.add(1, 'days');
                j++;
              }
            }

            // Larghezza più grande per il giorno selezionato e il successivo
            const width = table.offsetWidth;
            if (daysOfWeek !== 6)
              if ((children[daysOfWeek + 1] as any).style.width !== '0px')
                (children[daysOfWeek + 1] as any).style.width = `${
                  width / 15
                }px`;
            (children[daysOfWeek] as any).style.width = `${width / 10}px`;
          }
        }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [view, selectedDate, calendarPerUser, scheduleElementId, isLoading]);
}
